import { DataTable } from '@/components/common/data-table/data-table';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { useDataTable } from '@/components/common/data-table/use-data-table';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AuthContext } from '@/context/auth.context';
import {
  useFeatureFlag,
  useOverrideFeatureFlags,
} from '@/integrations/feature-flags/feature-flags';
import { FeatureFlagKey, FEATURE_FLAGS } from '@erp/shared-types';
import DashboardLayout from '@/layouts/dashboard/DashboardLayout';
import { Switch } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { XIcon } from '@phosphor-icons/react';
import { useActiveFeatureFlags } from 'posthog-js/react';
import { useContext, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  useListFeatureFlags,
  useSetFeatureFlagOverride,
  useRemoveFeatureFlagOverride,
  useClearAllFeatureFlagOverrides,
} from '@/api/rpc/testing';

type Flags<Key extends FeatureFlagKey> = {
  flag: Key;
  isActive: boolean;
  overridden: boolean;
};

export function FeatureFlagsPage() {
  const { currentUser, isFetchingAuthInitialStatus } = useContext(AuthContext);

  const isNavigating = useRef(false);
  const isGentlyAI = currentUser?.isAdmin;

  const navigate = useNavigate();

  if (!isFetchingAuthInitialStatus && !isGentlyAI && !isNavigating.current) {
    isNavigating.current = true;
    navigate({ to: '/' });
    return null;
  }

  return <FeatureFlagsPageContent />;
}

function FeatureFlagsPageContent() {
  const {
    clear,
    toggleFlagEnabled,
    // setVariant,
    // setPayload,
    flagsWithOverrides,
    removeOverride,
  } = useOverrideFeatureFlags();

  const activeFlagKeys = useActiveFeatureFlags();
  const { data: backendFlags, refetch: refetchBackendFlags } =
    useListFeatureFlags();
  const setBackendOverride = useSetFeatureFlagOverride();
  const removeBackendOverride = useRemoveFeatureFlagOverride();
  const clearAllBackendOverrides = useClearAllFeatureFlagOverrides();

  const columns: ColumnDef<Flags<FeatureFlagKey>>[] = useMemo(() => {
    return [
      {
        header: 'Flag',
        id: 'flag',
        cell: ({ row }) => {
          return <div>{row.original.flag}</div>;
        },
        accessorFn: (row) => row.flag,
      },
      {
        header: 'Active in PostHog (Frontend)?',
        id: 'isActive',
        cell: ({ row }) => {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div>{row.original.isActive ? 'Yes' : 'No'}</div>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {row.original.isActive
                    ? 'This flag is enabled in PostHog (frontend)'
                    : 'This flag is disabled in PostHog (frontend)'}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        },
      },

      {
        header: 'Enabled?',
        id: 'enabled',
        cell: function EnabledCell({ row }) {
          const { enabled } = useFeatureFlag(row.original.flag, false);
          return (
            <Switch
              checked={enabled}
              onChange={() => {
                const newEnabled = !enabled;
                toggleFlagEnabled(row.original.flag, newEnabled);
                // Sync to backend
                setBackendOverride.mutate(
                  {
                    flag: row.original.flag,
                    enabled: newEnabled,
                  },
                  {
                    onSuccess: () => {
                      refetchBackendFlags();
                    },
                  },
                );
              }}
            />
          );
        },
      },
      {
        header: 'Overriden?',
        id: 'overridden',
        cell: ({ row }) => {
          return (
            <>
              <Button
                variant="outline"
                color="primary"
                onClick={() => {
                  removeOverride(row.original.flag);
                  // Remove backend override
                  removeBackendOverride.mutate(
                    { flag: row.original.flag },
                    {
                      onSuccess: () => {
                        refetchBackendFlags();
                      },
                    },
                  );
                }}
                size="sm"
                disabled={!row.original.overridden}
                className="text-sm"
              >
                {row.original.overridden ? 'Active' : 'None'}

                <XIcon className="text-destructive" />
              </Button>
            </>
          );
        },
      },
      {
        header: 'Backend Value',
        id: 'backendValue',
        cell: ({ row }) => {
          const backendFlag = backendFlags?.flags.find(
            (f) => f.flag === row.original.flag,
          );

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  {backendFlag
                    ? backendFlag.enabled
                      ? 'Enabled'
                      : 'Disabled'
                    : 'N/A'}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <pre>{JSON.stringify(backendFlag, null, 2)}</pre>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        header: 'Variant',
        id: 'variantKey',
        cell: () => {
          return <div>N/A TODO</div>;
        },
      },
      {
        header: 'Payload',
        id: 'payload',
        cell: () => {
          return <div>N/A TODO</div>;
        },
      },
    ];
  }, [
    toggleFlagEnabled,
    removeOverride,
    setBackendOverride,
    removeBackendOverride,
    refetchBackendFlags,
    backendFlags,
  ]);

  const tableData: Flags<FeatureFlagKey>[] = useMemo(() => {
    // Merge flags from: frontend PostHog, backend, and FEATURE_FLAGS definition
    const allKeys = new Set([
      ...activeFlagKeys,
      ...Object.keys(FEATURE_FLAGS),
      ...(backendFlags?.flags.map((f) => f.flag) || []),
    ]);

    return Array.from(allKeys).map((flag) => ({
      flag: flag as FeatureFlagKey,
      isActive: activeFlagKeys.includes(flag),
      overridden: !!flagsWithOverrides?.some((f) => f.flag === flag),
    }));
  }, [activeFlagKeys, flagsWithOverrides, backendFlags]);

  const table = useDataTable({
    columns,
    data: tableData,
    options: {
      initialState: {
        pagination: { pageIndex: 0, pageSize: 100 },
      },
    },
  });

  const { enabled: isTestFlagEnabled } = useFeatureFlag('test-flag', false);

  return (
    <DashboardLayout>
      <div className="text-base font-mono">
        Sanity Check: Test flag is {isTestFlagEnabled ? 'enabled' : 'disabled'}
      </div>
      <div className="flex flex-col gap-2">
        <DataTableToolbar
          table={table}
          search={{
            placeholder: 'Search flags',
            value: table.getState().globalFilter ?? '',
            onChange: (event) => {
              table.setGlobalFilter(event.target.value);
            },
          }}
        >
          <Button
            variant="destructive"
            color="primary"
            size="sm"
            onClick={() => {
              clear();
              // Clear backend overrides
              clearAllBackendOverrides.mutate(
                {},
                {
                  onSuccess: () => {
                    refetchBackendFlags();
                  },
                },
              );
            }}
            disabled={!flagsWithOverrides?.length}
          >
            Clear All Overrides
          </Button>
        </DataTableToolbar>
        <DataTable table={table} />
      </div>
    </DashboardLayout>
  );
}
